import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import StatCard from './StatCard';
import { FaChartLine, FaArrowDown, FaArrowUp } from 'react-icons/fa';
import './PaceAnalysis.css';

const PaceAnalysis = ({ lapData, driverName, driverColor = '#36a2eb' }) => {
  const paceData = useMemo(() => {
    if (!lapData || lapData.length === 0) return null;

    const validLaps = lapData
      .filter(lap => lap.lap_duration && lap.lap_duration > 0)
      .sort((a, b) => a.lap_number - b.lap_number);

    if (validLaps.length === 0) return null;

    const lapNumbers = validLaps.map(lap => lap.lap_number);
    const lapTimes = validLaps.map(lap => lap.lap_duration);

    // Calculate race pace (average excluding outliers)
    const sortedTimes = [...lapTimes].sort((a, b) => a - b);
    const q1 = sortedTimes[Math.floor(sortedTimes.length * 0.25)];
    const q3 = sortedTimes[Math.floor(sortedTimes.length * 0.75)];
    const iqr = q3 - q1;
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;

    const racePaceLaps = lapTimes.filter(
      time => time >= lowerBound && time <= upperBound
    );
    const racePace = racePaceLaps.length > 0
      ? racePaceLaps.reduce((a, b) => a + b, 0) / racePaceLaps.length
      : lapTimes.reduce((a, b) => a + b, 0) / lapTimes.length;

    // Calculate pace degradation
    const firstHalf = lapTimes.slice(0, Math.floor(lapTimes.length / 2));
    const secondHalf = lapTimes.slice(Math.floor(lapTimes.length / 2));
    const firstHalfAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
    const secondHalfAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
    const degradation = secondHalfAvg - firstHalfAvg;

    // Calculate trend (simple linear regression slope)
    const n = lapTimes.length;
    const sumX = lapNumbers.reduce((a, b) => a + b, 0);
    const sumY = lapTimes.reduce((a, b) => a + b, 0);
    const sumXY = lapNumbers.reduce((sum, x, i) => sum + x * lapTimes[i], 0);
    const sumXX = lapNumbers.reduce((sum, x) => sum + x * x, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const trend = slope > 0.01 ? 'degrading' : slope < -0.01 ? 'improving' : 'stable';

    return {
      lapNumbers,
      lapTimes,
      racePace: racePace.toFixed(3),
      degradation: degradation.toFixed(3),
      trend,
      fastestLap: Math.min(...lapTimes).toFixed(3),
      averageLap: (lapTimes.reduce((a, b) => a + b, 0) / lapTimes.length).toFixed(3),
    };
  }, [lapData]);

  if (!paceData) {
    return (
      <div className="pace-analysis">
        <h3>Pace Analysis</h3>
        <p>No data available</p>
      </div>
    );
  }

  // Helper function to add alpha to hex color
  const addAlpha = (color, alpha) => {
    if (!color) return `rgba(54, 162, 235, ${alpha})`;
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color;
  };

  const chartData = {
    labels: paceData.lapNumbers,
    datasets: [
      {
        label: `${driverName} Lap Times`,
        data: paceData.lapTimes,
        borderColor: driverColor,
        backgroundColor: addAlpha(driverColor, 0.25),
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'Race Pace',
        data: paceData.lapNumbers.map(() => parseFloat(paceData.racePace)),
        borderColor: '#ffd700',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        pointRadius: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#ffffff',
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: driverColor,
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Lap Number',
          color: '#ffffff',
        },
        ticks: {
          color: '#ffffff',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Lap Time (s)',
          color: '#ffffff',
        },
        ticks: {
          color: '#ffffff',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  };

  return (
    <div className="pace-analysis">
      <h3 className="pace-title">{driverName} - Pace Analysis</h3>
      <div className="pace-stats-grid">
        <StatCard
          title="Race Pace"
          value={paceData.racePace}
          unit="s"
          icon={<FaChartLine />}
        />
        <StatCard
          title="Fastest Lap"
          value={paceData.fastestLap}
          unit="s"
        />
        <StatCard
          title="Pace Degradation"
          value={paceData.degradation}
          unit="s"
          trend={{
            type: parseFloat(paceData.degradation) < 0.5 ? 'positive' : 'negative',
            icon: parseFloat(paceData.degradation) < 0.5 ? <FaArrowDown /> : <FaArrowUp />,
            text: parseFloat(paceData.degradation) < 0.5 ? 'Good' : 'Degrading',
          }}
        />
        <StatCard
          title="Trend"
          value={paceData.trend}
          trend={{
            type: paceData.trend === 'improving' ? 'positive' : paceData.trend === 'degrading' ? 'negative' : 'neutral',
            text: paceData.trend === 'improving' ? 'Improving' : paceData.trend === 'degrading' ? 'Degrading' : 'Stable',
          }}
        />
      </div>
      <div className="pace-chart-container">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default PaceAnalysis;

